import React, {FC, useState, useCallback, useRef, useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {Dropdown} from 'react-bootstrap'
import {toast} from 'react-toastify'
import styles from './CenterPanel.module.scss'
import {ChatType, ConversationData, TeamMember, Message} from '../../types/inbox-v3.types'
import InboxChatInnerV3 from './InboxChatInnerV3'
import MessageInputV3 from '../Shared/MessageInputV3'
import RmsInbox from '../../../inbox-v2/RmsInbox'
import {getSingleRmsChat} from '../../../inbox-v2/InboxSlice/InboxSlice'
import {getAllUserDetails} from '../../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {useLazyGetSingleReceiverMessageExportQuery} from '../../../../../../services/cellcast'
import {useLazyGetRawContactListQuery} from '../../../../../../services/cellcast.v2'
import {ENV} from '../../../../../Utils/_helpers/EnvHelper'
import { StringArraySupportOption } from 'prettier'

const API_DOMAIN = ENV().REACT_APP_DOMAIN

/** Helpers exposed by InboxChatInnerV3 for message management */
export interface ChatMessageHelpers {
  prependMessage: (message: Message) => void
  fetchAndPrependMessage: (messageId: string) => Promise<void>
}

interface CenterPanelProps {
  chatType: ChatType
  selectedReceiver: string | null
  selectedConversation: ConversationData | null
  /** Conversation ID for the new API */
  selectedConversationId?: string | null
  messengerName: string
  onBack?: () => void
  isMobile: boolean
  // Action handlers
  teamMembers?: TeamMember[]
  isLoadingTeamMembers?: boolean
  assignedTo?: TeamMember | null
  onArchive?: (id: string) => void
  onFlag?: (id: string) => void
  onAssign?: (id: string, assignee: TeamMember | null) => void
  onUnassign?: (id: string) => void
  onTag?: (id: string) => void
  onUnsubscribe?: (id: string) => void
  onSubscribe?: (id: string) => void
  onExport?: (id: string) => void
  onToggleRightPanel?: () => void
  isRightPanelOpen?: boolean
  // Message handlers
  onSendMessage?: (
    receiver: string,
    message: string,
    sender: string,
    senderType: string,
    longUrls?: any[]
  ) => void
  onSendScheduleMessage?: (
    receiver: string,
    message: string,
    sender: string,
    senderType: string,
    scheduleAt: Date,
    longUrls?: any[]
  ) => void
  isSendingMessage?: boolean
  /** Callback when message helpers are ready (for parent component to use prependMessage) */
  onMessageHelpersReady?: (helpers: ChatMessageHelpers) => void
  // RMS props
  selectedRmsId?: string | null
  selectedRmsNumber?: string
}

const CenterPanel: FC<CenterPanelProps> = ({
  chatType,
  selectedReceiver,
  selectedConversation,
  selectedConversationId,
  messengerName,
  onBack,
  isMobile,
  teamMembers = [],
  isLoadingTeamMembers = false,
  assignedTo = null,
  onArchive,
  onFlag,
  onAssign,
  onUnassign,
  onTag,
  onUnsubscribe,
  onSubscribe,
  onExport,
  onToggleRightPanel,
  isRightPanelOpen = false,
  onSendMessage,
  onSendScheduleMessage,
  isSendingMessage = false,
  onMessageHelpersReady,
  // RMS props
  selectedRmsId,
  selectedRmsNumber,
}) => {
  const [showAddEmailSources, setShowAddEmailSources] = useState(false)
  const [showEmailSetupGuide, setShowEmailSetupGuide] = useState(false)

  // AI Suggestions state
  const [welcomeMessage, setWelcomeMessage] = useState<string>('')
  const [showAiSuggestion, setShowAiSuggestion] = useState<boolean>(false)
  const [fetchRawContacts] = useLazyGetRawContactListQuery()

  // Get user details to check if bcard user
  const userDetail = useSelector(getAllUserDetails)

  // Check if user is a bcard user (has B-card tag and Wallet Card feature)
  const isBcardUser = useMemo(() => {
    return userDetail?.tagNames?.includes('B-card') && userDetail?.features?.includes('Wallet Card')
  }, [userDetail])

  // Get RMS single chat data from Redux
  const rmsSingleData = useSelector(getSingleRmsChat)

  // Export API hook
  const [exportSingleReceiverMessage] = useLazyGetSingleReceiverMessageExportQuery()

  // Store the message helpers from InboxChatInnerV3
  const messageHelpersRef = useRef<ChatMessageHelpers | null>(null)

  // Handle when InboxChatInnerV3 provides its message helpers
  const handleMessagesReady = useCallback(
    (helpers: {
      prependMessage: (message: Message) => void
      fetchAndPrependMessage: (messageId: string) => Promise<void>
    }) => {
      messageHelpersRef.current = helpers
      // Notify parent component
      if (onMessageHelpersReady) {
        onMessageHelpersReady(helpers)
      }
    },
    [onMessageHelpersReady]
  )

  // Fetch welcomeMessage from Raw Contacts when receiver changes (only for bcard users)
  useEffect(() => {
    setWelcomeMessage('')
    setShowAiSuggestion(false)

    // Only fetch welcome message for bcard users
    if (!selectedReceiver || !isBcardUser) return

    fetchRawContacts({search: String(selectedReceiver), page: 1, limit: 1})
      .then((res: any) => {
        if ('data' in res) {
          const items = res?.data?.data?.items || []
          if (items.length > 0) {
            const first = items[0] as any
            const wm = first?.welcomeMessage || ''
            if (typeof wm === 'string' && wm.trim().length > 0) {
              setWelcomeMessage(wm)
            }
          }
        }
      })
      .catch(() => {})
  }, [selectedReceiver, fetchRawContacts, isBcardUser])

  // Get conversation ID (use conversation._id, not contacts._id)
  // conversation._id is used for API calls (archive, flag, etc.)
  // contacts._id is the contact ID (different from conversation ID)
  const conversationId = selectedConversation?._id || ''
  const contactId = selectedConversation?.contacts._id || ''
  const isArchived = selectedConversation?.contacts.isArchived || false
  const isFlagged = selectedConversation?.contacts.isFlagged || false
  const isSubscribed = selectedConversation?.contacts.isSubscribed

  // Get initials for avatar
  const getInitials = () => {
    if (!selectedConversation) return '?'
    const fields = selectedConversation.contactgroupmaps.fields
    const firstName = fields.find((f) => f.name === 'First Name')?.value || ''
    const lastName = fields.find((f) => f.name === 'Last Name')?.value || ''

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase()
    }
    return selectedConversation.receiver.slice(-2)
  }

  // Action handlers
  const handleArchive = () => {
    if (onArchive && conversationId) {
      onArchive(conversationId)
    }
  }

  const handleFlag = () => {
    if (onFlag && conversationId) {
      onFlag(conversationId)
    }
  }

  const handleTag = () => {
    if (onTag && conversationId) {
      onTag(conversationId)
    }
  }

  const handleUnsubscribe = () => {
    if (onUnsubscribe && selectedConversation?._id) {
      onUnsubscribe(selectedConversation._id)
    }
  }

  const handleExport = () => {
    if (selectedReceiver) {
      exportSingleReceiverMessage({
        receiver: selectedReceiver,
        isExport: true,
      })
        .unwrap()
        .then((res: any) => {
          toast.success('Success Export message')
          window.open(`${API_DOMAIN}${res?.data?.downloadurl}`, '_self')
        })
        .catch((e) => {
          toast.error('Failed to export message')
        })
    }
  }

  const handleAssign = (member: TeamMember) => {
    if (onAssign && conversationId) {
      onAssign(conversationId, member)
    }
  }

  const handleUnassign = () => {
    if (onUnassign && conversationId) {
      onUnassign(conversationId)
    }
  }

  const handleSubscribe = () => {
    if (onSubscribe && selectedConversation?._id) {
      onSubscribe(selectedConversation._id)
    }
  }

  // Handle sending message from MessageInputV3
  const handleSendMessage = (
    message: string,
    sender: string,
    senderType: string,
    longUrls?: any[]
  ) => {
    if (onSendMessage && selectedReceiver) {
      onSendMessage(selectedReceiver, message, sender, senderType, longUrls)
    }
  }

  const handleSendScheduleMessage = (
    message: string,
    sender: string,
    senderType: string,
    scheduleAt: Date,
    longUrls?: any[]
  ) => {
    if (onSendScheduleMessage && selectedReceiver) {
      onSendScheduleMessage(selectedReceiver, message, sender, senderType, scheduleAt, longUrls)
    }
  }

  // Empty state for RMS Chat
  if (chatType === 'rms') {
    if (!selectedRmsId) {
      return (
        <div className={styles.centerPanelContainer}>
          <div className={styles.emptyState}>
            <i className={`bi bi-chat-square-text ${styles.emptyIcon}`}></i>
            <h4>Select an RMS conversation</h4>
            <p>Choose a conversation from the list to view RMS messages</p>
          </div>
        </div>
      )
    }
    // Render RMS chat
    return (
      <div className={styles.centerPanelContainer}>
        <div className={styles.chatHeader}>
          <div className={styles.headerLeft}>
            {isMobile && onBack && (
              <button className={styles.backButton} onClick={onBack}>
                <i className='bi bi-arrow-left'></i>
              </button>
            )}
            <div className={styles.contactAvatar}>{selectedRmsNumber?.slice(-2) || 'RM'}</div>
            <div className={styles.contactInfo}>
              <div className={styles.contactName}>RMS Chat</div>
              <div className={styles.contactNumber}>{selectedRmsNumber}</div>
            </div>
          </div>
        </div>
        <div className={styles.chatBody}>
          <RmsInbox data={selectedRmsNumber} rmsdata={rmsSingleData || []} />
        </div>
      </div>
    )
  }

  // Helper for copying email address
  const handleCopyEmailAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Email address copied to clipboard!')
  }

  // Empty state when no conversation selected (Inbox Chat)
  if (!selectedReceiver || !selectedConversation) {
    return (
      <div className={styles.centerPanelContainer}>
        <div className={styles.emptyState}>
          <i className={`bi bi-chat-dots ${styles.emptyIcon}`}></i>
          <h4>Select a conversation</h4>
          <p>Choose a conversation from the list to start messaging</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.centerPanelContainer}>
      {/* Chat Header */}
      <div className={styles.chatHeader}>
        <div className={styles.headerLeft}>
          {isMobile && onBack && (
            <button className={styles.backButton} onClick={onBack}>
              <i className='bi bi-arrow-left'></i>
            </button>
          )}
          <div className={styles.contactAvatar}>{getInitials()}</div>
          <div className={styles.contactInfo}>
            <div className={styles.contactName}>{messengerName}</div>
            {/* Only show number if contact has a name (not just the number) */}
            {messengerName !== selectedReceiver && (
              <div className={styles.contactNumber}>{selectedReceiver}</div>
            )}
          </div>
        </div>
        <div className={styles.headerRight}>
          {/* Assigned To - Simple text label, hidden when unassigned */}
          {assignedTo && (
            <span className={styles.assignedToLabel}>Assigned to: {assignedTo.name}</span>
          )}

          {/* Call button */}
          <button
            className={styles.headerButton}
            title='Call'
            onClick={() => {
              if (selectedReceiver) {
                window.location.href = `tel:${selectedReceiver}`
              }
            }}
          >
            <i className='bi bi-telephone'></i>
          </button>

          {/* Three-dot action menu */}
          <Dropdown align='end'>
            <Dropdown.Toggle as='button' id='center-panel-dropdown' className={styles.actionDropdown}>
              <i className='bi bi-three-dots-vertical fs-5'></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
            {/* Flag option */}
            <Dropdown.Item
              className={`${styles.menuItem} ${isFlagged ? styles.flaggedItem : ''}`}
              onClick={handleFlag}
            >
              <i className={`bi ${isFlagged ? 'bi-flag-fill' : 'bi-flag'} me-2`}></i>
              {isFlagged ? 'Unflag' : 'Flag'}
            </Dropdown.Item>

            {/* Archive option */}
            <Dropdown.Item className={styles.menuItem} onClick={handleArchive}>
              <i className={`bi ${isArchived ? 'bi-archive-fill' : 'bi-archive'} me-2`}></i>
              {isArchived ? 'Unarchive' : 'Archive'}
            </Dropdown.Item>

            <Dropdown.Divider />

            {/* Assign To submenu - always show */}
            <Dropdown drop='start'>
              <Dropdown.Toggle as='button' id='assign-dropdown' className={styles.assignToggle}>
                <span className={styles.assignLabel}>
                  <div>
                    <i className='bi bi-person-plus me-2'></i>
                    {assignedTo ? `Assigned: ${assignedTo.name.split(' ')[0]}` : 'Assign to'}
                  </div>
                  <i className='bi bi-chevron-right'></i>
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.assignSubmenu}>
                {isLoadingTeamMembers ? (
                  <div className={styles.loadingTeamMembers}>
                    <span className='spinner-border spinner-border-sm me-2' role='status' />
                    Loading...
                  </div>
                ) : teamMembers.length > 0 ? (
                  <>
                    {teamMembers.map((member) => (
                      <Dropdown.Item
                        key={member._id}
                        onClick={() => handleAssign(member)}
                        className={assignedTo?._id === member._id ? styles.activeItem : ''}
                      >
                        <div className={styles.teamMemberOption}>
                          <div className={styles.teamMemberAvatar}>
                            {member.avatar ? (
                              <img src={member.avatar} alt={member.name} />
                            ) : (
                              <span>{member.name.charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <span>{member.name}</span>
                          {assignedTo?._id === member._id && (
                            <i className='bi bi-check-lg text-primary ms-auto'></i>
                          )}
                        </div>
                      </Dropdown.Item>
                    ))}
                    {assignedTo && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={handleUnassign} className={styles.unassignMenuItem}>
                          <i className='bi bi-x-circle me-2'></i>
                          Unassign
                        </Dropdown.Item>
                      </>
                    )}
                  </>
                ) : (
                  <div className={styles.noTeamMembersSmall}>No team members</div>
                )}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown.Item className={styles.menuItem} onClick={handleTag}>
              <i className='bi bi-tag me-2'></i>Add Tag
            </Dropdown.Item>

            {/* Subscribe/Unsubscribe based on current status */}
            {isSubscribed === false ? (
              <Dropdown.Item
                className={`${styles.menuItem} text-success`}
                onClick={handleSubscribe}
              >
                <i className='bi bi-person-check me-2'></i>Subscribe
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                className={`${styles.menuItem} text-danger`}
                onClick={handleUnsubscribe}
              >
                <i className='bi bi-person-x me-2'></i>Unsubscribe
              </Dropdown.Item>
            )}

            <Dropdown.Item className={styles.menuItem} onClick={handleExport}>
              <i className='bi bi-download me-2'></i>Export
            </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* Panel toggle button - positioned after three-dot menu like sidebar collapse */}
          <button
            className={`${styles.panelToggleButton} ${isRightPanelOpen ? styles.active : ''}`}
            onClick={onToggleRightPanel}
            title={isRightPanelOpen ? 'Close contact details' : 'Open contact details'}
          >
            <i
              className={`bi ${
                !isRightPanelOpen ? 'bi-chevron-double-right' : 'bi-chevron-double-left'
              }`}
            ></i>
          </button>
        </div>
      </div>

      {/* Chat Content - Using InboxChatInnerV3 */}
      <div className={styles.chatContent}>
        {selectedReceiver && (
          <InboxChatInnerV3
            key={selectedConversationId || selectedReceiver}
            data={selectedReceiver}
            messengerName={messengerName}
            firstTime={true}
            conversationId={selectedConversationId}
            onMessagesReady={handleMessagesReady}
          />
        )}
      </div>

      {/* New Message Input */}
      <div className={styles.messageInputWrapper}>
        <MessageInputV3
          receiver={selectedReceiver || ''}
          onSend={handleSendMessage}
          onSendSchedule={handleSendScheduleMessage}
          isSending={isSendingMessage}
          disabled={!selectedReceiver}
          welcomeMessage={welcomeMessage}
          showAiSuggestion={showAiSuggestion}
          onToggleAiSuggestion={() => setShowAiSuggestion(!showAiSuggestion)}
          onUseAiSuggestion={() => setShowAiSuggestion(false)}
          isBcardUser={isBcardUser}
        />
      </div>
    </div>
  )
}

export default CenterPanel